import { Chip, MenuItem, Select, Stack, Typography } from "@mui/material";
import { requiredStyles } from "./BasicTab";
import {
  DependencyFormListItem,
  useDependencyParentPicker,
} from "./useDependencyParentPicker";
import { Question } from "../../../types/StudyFormById.types";

type Props = {
  parentFormId?: string;
  parentQuestionId?: string;
  excludeQuestionId?: string;
  studyId?: string;
  formCategory?: string;
  formLevel?: string;
  currentFormId?: string;
  currentFormLabel?: string;
  readOnly?: boolean;
  showFormPicker?: boolean;
  onParentFormChange: (formId: string) => void;
  onParentQuestionChange: (questionId: string, question?: Question) => void;
  disableQuestionIds?: string[];
};

const DependencyParentPicker = ({
  parentFormId,
  parentQuestionId,
  excludeQuestionId,
  studyId,
  formCategory,
  formLevel,
  currentFormId,
  currentFormLabel,
  readOnly = false,
  showFormPicker = false,
  onParentFormChange,
  onParentQuestionChange,
  disableQuestionIds = [],
}: Props) => {
  const { formList, questionList, setSelectedQuestion } =
    useDependencyParentPicker({
      parentFormId,
      parentQuestionId,
      excludeQuestionId,
      studyId,
      formCategory,
      formLevel,
      currentFormId,
      currentFormLabel,
    });

  return (
    <Stack
      gap={1}
      direction={"row"}
      alignItems={"center"}
      sx={{ width: "100%" }}
    >
      {showFormPicker && (
        <Stack gap={1} sx={{ width: "50%" }}>
          <Typography variant="body1" fontWeight={"regular"} sx={requiredStyles}>
            Form
          </Typography>

          <Select
            readOnly={readOnly}
            fullWidth
            id="parentFormId"
            value={parentFormId || ""}
            onChange={(e) => onParentFormChange(e.target.value)}
          >
            {formList?.length ? (
              formList.map((item: DependencyFormListItem) => (
                <MenuItem key={item.formId} value={item.formId}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{ width: "100%", minWidth: 0 }}
                  >
                    <Typography
                      component="span"
                      variant="body1"
                      noWrap
                      sx={{ minWidth: 0 }}
                    >
                      {item.label}
                    </Typography>
                    {item.status === "active" && (
                      <Chip
                        size="small"
                        label="Active"
                        sx={{
                          height: 20,
                          fontSize: 11,
                          fontWeight: 600,
                          backgroundColor: "#D1FAE5",
                          color: "#065F46",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Stack>
                </MenuItem>
              ))
            ) : (
              <MenuItem key={"None"} value={""} disabled>
                {"No Forms available"}
              </MenuItem>
            )}
          </Select>
        </Stack>
      )}

      <Stack gap={1} sx={{ width: showFormPicker ? "50%" : "100%" }}>
        <Typography variant="body1" fontWeight={"regular"} sx={requiredStyles}>
          Question
        </Typography>

        <Select
          readOnly={readOnly}
          fullWidth
          id="parentQuestionId"
          value={parentQuestionId || ""}
          onChange={(e) => {
            const question = questionList.find((q) => q.id === e.target.value);
            if (question) {
              setSelectedQuestion(question);
            }
            onParentQuestionChange(e.target.value, question);
          }}
        >
          {questionList.length ? (
            questionList.map((item) => (
              <MenuItem
                key={item.id}
                value={item.id}
                disabled={disableQuestionIds.includes(item.id)}
              >
                {item.varname} -{" "}
                {item.label.length > 50
                  ? item.label.slice(0, 50) + "..."
                  : item.label}
              </MenuItem>
            ))
          ) : (
            <MenuItem key={"None"} value={""} disabled>
              {"No Questions available"}
            </MenuItem>
          )}
        </Select>
      </Stack>
    </Stack>
  );
};

export default DependencyParentPicker;
